import { Check } from '@phosphor-icons/react';
import { Badge, Button, cn, Inline, Stack } from '@reelplicate/ui';

export type Plan = {
  id: string;
  name: string;
  description: string;
  price: string;
  period: string;
  cta: string;
  /** Free trial days at the selected billing period; 0 is none. */
  trialDays: number;
  features: string[];
  highlighted?: boolean;
};

/**
 * Figma LP / Pricing Card (90:356) as the plan step uses it: radius/2xl,
 * 32px padding, 24px gaps. Default: bg/surface + border/subtle, outline CTA.
 * Highlighted: bg/inverse, text/inverse (secondary copy at 72%), primary CTA
 * and a solid brand "Most popular" badge. Mirrors the marketing card.
 */
export function PlanCard({
  plan,
  onChoose,
  disabled = false,
}: {
  plan: Plan;
  onChoose: () => void;
  disabled?: boolean;
}) {
  const { highlighted = false } = plan;
  const muted = highlighted ? 'text-text-inverse opacity-72' : 'text-text-secondary';
  return (
    <Stack
      as="article"
      gap={6}
      aria-labelledby={`plan-${plan.id}`}
      className={cn(
        'flex-1 rounded-2xl border p-8',
        highlighted
          ? 'bg-bg-inverse border-border-inverse text-text-inverse'
          : 'bg-bg-surface border-border-subtle',
      )}
    >
      <Stack gap={2}>
        <Inline align="between" gap={2}>
          <h2 id={`plan-${plan.id}`} className="text-heading-h4">
            {plan.name}
          </h2>
          {highlighted && (
            <Badge tone="brand" variant="solid" size="sm">
              Most popular
            </Badge>
          )}
        </Inline>
        <p className={cn('text-body-md', muted)}>{plan.description}</p>
      </Stack>
      <Inline gap={1.5} alignY="baseline">
        <span className="text-display-sm tracking-[-1.5px]">{plan.price}</span>
        <span className={cn('text-body-md', muted)}>{plan.period}</span>
      </Inline>
      <Stack gap={2} align="center">
        <Button
          size="lg"
          variant={highlighted ? 'primary' : 'outline'}
          className="w-full"
          onClick={onChoose}
          disabled={disabled}
        >
          {plan.cta}
        </Button>
        <p className={cn('text-caption', muted)}>
          {plan.trialDays > 0
            ? `${plan.trialDays}-day free trial · Cancel anytime`
            : 'Cancel anytime'}
        </p>
      </Stack>
      <div
        aria-hidden
        className={cn(
          'h-px',
          highlighted ? 'bg-text-inverse opacity-16' : 'bg-border-subtle',
        )}
      />
      <Stack gap={3}>
        <p className="text-label-sm">Includes</p>
        <Stack as="ul" gap={3}>
          {plan.features.map((feature) => (
            <Inline as="li" key={feature} className="gap-2.5">
              <Check
                aria-hidden
                className={cn(
                  'size-[18px] shrink-0',
                  highlighted ? 'text-brand-primary' : 'text-icon-brand',
                )}
              />
              <span className="text-body-md flex-1">{feature}</span>
            </Inline>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
}
